import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import {
    ICriterion, IRatingPart, IReviewDetail, IReviewPart, IReviewPartRequest,
} from '../../interfaces/appraisal.interface';
import { displayUsername } from '../../utils/userUtils';
import { bandColor, bandForScore, formatScore, isManagerDone } from './bands';
import { formatDate, isManagerOverdue, isSelfOverdue } from './dates';
import { StatusChips } from './ReviewTable';
import ConfirmDialog from './ConfirmDialog';

const SCORES = [1, 2, 3, 4, 5];
const COMMENT_MAX = 2000;
const OVERALL_MAX = 4000;

interface Draft {
    ratings: Record<string, { score?: number; comment: string }>;
    overallComment: string;
}

const draftFrom = (part: IReviewPart | null | undefined): Draft => ({
    overallComment: part?.overallComment ?? '',
    ratings: Object.fromEntries((part?.ratings ?? []).map(r => [
        r.criterionId, { score: r.score ?? undefined, comment: r.comment ?? '' },
    ])),
});

const toRequest = (draft: Draft, criteria: ICriterion[]): IReviewPartRequest => ({
    overallComment: draft.overallComment.trim() || undefined,
    ratings: criteria.map(c => {
        const r = draft.ratings[c.id];
        return { criterionId: c.id, score: r?.score, comment: r?.comment.trim() || undefined };
    }),
});

const ratingOf = (part: IReviewPart | null | undefined, criterionId: string): IRatingPart | undefined =>
    part?.ratings.find(r => r.criterionId === criterionId);

const ReadOnlyRating: React.FC<{ label: string; rating?: IRatingPart }> = ({ label, rating }) => {
    const { t } = useTranslation('appraisal');
    return (
        <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body1" fontWeight={600}>
                {rating?.score ? t('review.ratingValue', { score: rating.score }) : '—'}
            </Typography>
            {rating?.comment && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{rating.comment}</Typography>}
        </Box>
    );
};

const DueLine: React.FC<{ label: string; overdue: boolean }> = ({ label, overdue }) => {
    const { t } = useTranslation('appraisal');
    return (
        <Typography variant="body2" sx={{ color: overdue ? 'warning.main' : 'text.secondary', fontWeight: overdue ? 600 : undefined }}>
            {label}
            {overdue && <Chip size="small" color="warning" label={t('overdue')} sx={{ ml: 1 }} />}
        </Typography>
    );
};

/** One review: the self form, the reviewer's form, or the read-only result, depending on who is looking. */
const ReviewPage: React.FC = () => {
    const { reviewId } = useParams<{ reviewId: string }>();
    const toast = useToast();
    const { t } = useTranslation(['appraisal', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [detail, setDetail] = useState<IReviewDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState<Draft>(draftFrom(null));
    const [busy, setBusy] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [ackOpen, setAckOpen] = useState(false);
    const [ackComment, setAckComment] = useState('');

    const applyDetail = (d: IReviewDetail) => {
        setDetail(d);
        setDraft(draftFrom(d.viewerRole === 'REVIEWER' ? d.manager : d.viewerRole === 'EMPLOYEE' ? d.self : null));
    };

    useEffect(() => {
        if (!reviewId) return;
        (async () => {
            try {
                applyDetail(await AppraisalService.getReview(institutionId, reviewId));
            } catch (err) {
                toast.showApiError(err, t('review.errors.load'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, reviewId]);

    const criteria = useMemo(
        () => (detail?.criteria ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
        [detail],
    );

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;
    }
    if (!detail) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{t('review.errors.load')}</Alert></Box>;
    }

    const active = detail.cycleStatus === 'ACTIVE';
    const managerDone = isManagerDone(detail.status);
    const editingSelf = detail.viewerRole === 'EMPLOYEE' && active && detail.status === 'PENDING';
    const editingManager = detail.viewerRole === 'REVIEWER' && active
        && (detail.status === 'PENDING' || detail.status === 'SELF_SUBMITTED');
    const editing = editingSelf || editingManager;
    const selfSubmitted = !!detail.self?.submittedAt;
    // The employee never sees an unsubmitted manager part, whatever the server sends.
    const managerVisible = !!detail.manager && (detail.viewerRole !== 'EMPLOYEE' || managerDone);
    const showSelf = !editingSelf && !!detail.self && (selfSubmitted || detail.viewerRole === 'EMPLOYEE');
    const showManager = !editingManager && managerVisible;
    const rated = criteria.filter(c => draft.ratings[c.id]?.score).length;
    const canSubmit = criteria.length > 0 && rated === criteria.length;
    const employeeName = displayUsername(detail.employee.name);

    const backTo = detail.viewerRole === 'EMPLOYEE' ? '/appraisals/mine'
        : detail.viewerRole === 'REVIEWER' ? '/appraisals/to-write'
            : `/appraisals/cycles/${detail.cycleId}`;

    const setRating = (criterionId: string, patch: Partial<{ score?: number; comment: string }>) =>
        setDraft(d => ({
            ...d,
            ratings: {
                ...d.ratings,
                [criterionId]: { ...(d.ratings[criterionId] ?? { comment: '' }), ...patch },
            },
        }));

    const saveDraft = async () => {
        setBusy(true);
        try {
            const body = toRequest(draft, criteria);
            const saved = editingSelf
                ? await AppraisalService.saveSelf(institutionId, detail.id, body)
                : await AppraisalService.saveManager(institutionId, detail.id, body);
            setDetail(saved);
            toast.showSuccess(t('review.draftSaved'));
        } catch (err) {
            toast.showApiError(err, t('review.errors.save'));
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        setBusy(true);
        try {
            const body = toRequest(draft, criteria);
            const saved = editingSelf
                ? await AppraisalService.submitSelf(institutionId, detail.id, body)
                : await AppraisalService.submitManager(institutionId, detail.id, body);
            applyDetail(saved);
            setConfirmSubmit(false);
            toast.showSuccess(t('review.submitted'));
        } catch (err) {
            toast.showApiError(err, t('review.errors.submit'));
        } finally {
            setBusy(false);
        }
    };

    const acknowledge = async () => {
        setBusy(true);
        try {
            applyDetail(await AppraisalService.acknowledge(institutionId, detail.id, ackComment.trim() || undefined));
            setAckOpen(false);
            toast.showSuccess(t('review.acknowledgedToast'));
        } catch (err) {
            toast.showApiError(err, t('review.errors.acknowledge'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000 }}>
            <Button component={RouterLink} to={backTo} startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>
                {t('common:actions.back')}
            </Button>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant="h5">{detail.cycleName}</Typography>
                        <Typography variant="body1">
                            {t('review.employee')}: <strong>{employeeName}</strong>
                            {detail.employee.departmentNames.length > 0 && ` · ${detail.employee.departmentNames.join(', ')}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('review.reviewer')}: {detail.reviewer ? displayUsername(detail.reviewer.name) : t('review.noReviewer')}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <StatusChips review={detail} />
                        <Box sx={{ mt: 1 }}>
                            <DueLine label={t('review.selfDue', { date: formatDate(detail.selfReviewDue) })} overdue={isSelfOverdue(detail)} />
                            <DueLine label={t('review.managerDue', { date: formatDate(detail.managerReviewDue) })} overdue={isManagerOverdue(detail)} />
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Stack spacing={1} sx={{ mb: 2 }}>
                {detail.cycleStatus === 'CLOSED' && <Alert severity="info">{t('review.closedReadOnly')}</Alert>}
                {detail.viewerRole === 'ADMIN' && <Alert severity="info">{t('review.adminReadOnly')}</Alert>}
                {detail.viewerRole === 'EMPLOYEE' && detail.status === 'SELF_SUBMITTED' && (
                    <Alert severity="info">{t('review.waitingForReviewer')}</Alert>
                )}
                {detail.viewerRole === 'EMPLOYEE' && managerDone && !selfSubmitted && (
                    <Alert severity="info">{t('review.selfClosed')}</Alert>
                )}
                {editingManager && !selfSubmitted && (
                    <Alert severity="warning">{t('review.managerForm.noSelfYet', { name: employeeName })}</Alert>
                )}
            </Stack>

            {managerDone && (
                <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">{t('review.result.score')}</Typography>
                        <Typography variant="h4">{formatScore(detail.managerScore)}</Typography>
                    </Box>
                    {detail.band && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" component="div">{t('review.result.band')}</Typography>
                            <Chip label={t(`band.${detail.band}`)} color={bandColor[detail.band]} />
                        </Box>
                    )}
                    {selfSubmitted && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">{t('review.result.selfScore')}</Typography>
                            <Typography variant="h6">{formatScore(detail.self?.score)}</Typography>
                        </Box>
                    )}
                </Paper>
            )}

            {editing && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">{editingSelf ? t('review.selfForm.title') : t('review.managerForm.title')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {editingSelf ? t('review.selfForm.intro') : t('review.managerForm.intro', { name: employeeName })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="body2">{t('review.scale')}</Typography>
                        {SCORES.map(s => (
                            <Chip key={s} size="small" variant="outlined"
                                label={t('review.scoreOption', { score: s, label: t(`band.${bandForScore(s)}`) })} />
                        ))}
                    </Box>
                </Box>
            )}

            <Stack spacing={1.5}>
                {criteria.map(c => (
                    <Paper key={c.id} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>{c.name}</Typography>
                            <Chip size="small" label={t('review.weight', { weight: c.weight })} />
                        </Box>
                        {c.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{c.description}</Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {showSelf && <ReadOnlyRating label={t('review.selfRating')} rating={ratingOf(detail.self, c.id)} />}
                            {editingManager && selfSubmitted && (
                                <ReadOnlyRating label={t('review.selfRating')} rating={ratingOf(detail.self, c.id)} />
                            )}
                            {showManager && <ReadOnlyRating label={t('review.managerRating')} rating={ratingOf(detail.manager, c.id)} />}
                        </Box>
                        {editing && (
                            <Box sx={{ mt: 1 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    color="primary"
                                    aria-label={t('review.ratingFor', { name: c.name })}
                                    value={draft.ratings[c.id]?.score ?? null}
                                    onChange={(_, v: number | null) => setRating(c.id, { score: v ?? undefined })}
                                >
                                    {SCORES.map(s => (
                                        <ToggleButton key={s} value={s} sx={{ width: 44 }}
                                            aria-label={t('review.scoreOption', { score: s, label: t(`band.${bandForScore(s)}`) })}>
                                            {s}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <TextField
                                    label={t('review.comment')}
                                    value={draft.ratings[c.id]?.comment ?? ''}
                                    onChange={e => setRating(c.id, { comment: e.target.value })}
                                    fullWidth multiline minRows={1} size="small" sx={{ mt: 1 }}
                                    inputProps={{ maxLength: COMMENT_MAX, 'aria-label': t('review.commentFor', { name: c.name }) }}
                                />
                            </Box>
                        )}
                    </Paper>
                ))}
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
                {(showSelf || (editingManager && selfSubmitted)) && detail.self?.overallComment && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">{t('review.selfOverall')}</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.self.overallComment}</Typography>
                    </Paper>
                )}
                {showManager && detail.manager?.overallComment && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">{t('review.managerOverall')}</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.manager.overallComment}</Typography>
                    </Paper>
                )}
                {detail.acknowledgement && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('review.acknowledgedOn', { date: formatDate(detail.acknowledgement.acknowledgedAt) })}
                        </Typography>
                        {detail.acknowledgement.comment && (
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.acknowledgement.comment}</Typography>
                        )}
                    </Paper>
                )}
            </Stack>

            {editing && (
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label={t('review.overallComment')}
                        value={draft.overallComment}
                        onChange={e => setDraft(d => ({ ...d, overallComment: e.target.value }))}
                        fullWidth multiline minRows={3}
                        inputProps={{ maxLength: OVERALL_MAX }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={saveDraft} disabled={busy}>{t('review.saveDraft')}</Button>
                        <Button variant="contained" onClick={() => setConfirmSubmit(true)} disabled={busy || !canSubmit}>
                            {editingSelf ? t('review.submitSelf') : t('review.submitManager')}
                        </Button>
                        {!canSubmit && (
                            <Typography variant="body2" color="text.secondary">
                                {t('review.rateAll', { rated, total: criteria.length })}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {detail.viewerRole === 'EMPLOYEE' && detail.status === 'MANAGER_SUBMITTED' && (
                <Box sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={() => { setAckComment(''); setAckOpen(true); }}>
                        {t('review.acknowledge')}
                    </Button>
                </Box>
            )}

            <ConfirmDialog
                open={confirmSubmit}
                title={t('review.confirmSubmit.title')}
                body={editingSelf ? t('review.confirmSubmit.selfBody')
                    : selfSubmitted ? t('review.confirmSubmit.managerBody') : t('review.confirmSubmit.managerBodyNoSelf')}
                confirmLabel={t('common:actions.submit')}
                busy={busy}
                onConfirm={submit}
                onClose={() => setConfirmSubmit(false)}
            />

            <Dialog open={ackOpen} onClose={busy ? undefined : () => setAckOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('review.acknowledgeDialog.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>{t('review.acknowledgeDialog.body')}</DialogContentText>
                    <TextField
                        label={t('review.acknowledgeDialog.comment')}
                        value={ackComment}
                        onChange={e => setAckComment(e.target.value)}
                        fullWidth multiline minRows={2}
                        inputProps={{ maxLength: COMMENT_MAX }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAckOpen(false)} disabled={busy}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={acknowledge} disabled={busy}>{t('review.acknowledge')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReviewPage;
